"use client";

import React, { useEffect, useContext, useCallback, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { UserContext } from "@/context/ContextProvider";
import Navbar from "@/components/Navbar/Navbar";
import { installChromeColorNavigationPatch } from "@/app/helpers/usePageChromeColor";
import AutoArticleStructuredData from "@/components/SEO/AutoArticleStructuredData";
import logo from "@/assets/Navbar/Logo.avif";
import {
  isCookieConsentSuppressedRoute,
  isNoTrackingRoute as isNoTrackingPath,
} from "@/constants/noTrackingRoutes";
import { usePathname } from "next/navigation";

const ToastHost = dynamic(() => import("./ToastHost"), { ssr: false });
// Footer bewusst MIT SSR. Die meisten Footer-Ziele stehen zwar schon in der
// Navbar; neu im Server-HTML sind nur Presse + Legal. Der Punkt ist, dass der
// Footer damit ueberhaupt erst ein verlaesslicher Ort fuer interne Links wird
// (Related-Content-Module liegen sonst hinter ssr:false und fehlen im HTML).
// Code-Splitting bleibt durch next/dynamic erhalten.
const Footer = dynamic(() => import("@/components/Footer/Footer"));
const SidebarRight = dynamic(() => import("@/components/Navbar/SidebarRight"), {
  ssr: false,
});
const CookiesModal = dynamic(
  () => import("@/components/Cookies/CookiesModal").then((m) => m.CookiesModal),
  { ssr: false }
);
const ChatWidget = dynamic(
  () => import("@/components/ChatWidget/ChatWidget"),
  { ssr: false }
);

export default function AppLayout({
  children,
  setIsSidebarRightOpen,
  isSidebarRightOpen,
  isNoTrackingRoute = false,
}) {
  // Foto-Flow-Chrome-Farbe: Navigations-Patch immer installieren, damit
  // auch der kalte Einstieg von ungetoenten Seiten die Zielfarbe VOR dem
  // Navigations-Event bekommt (ausserhalb des Flows No-Op).
  useEffect(() => {
    installChromeColorNavigationPatch();
  }, []);

  const { isCookiePopupOpen, isCookieConsentResolved, setIsCookiePopupOpen } =
    useContext(UserContext);
  const [mountToastHost] = useState(true);
  const [mountCookieModal, setMountCookieModal] = useState(false);
  const pathnameurl = usePathname();
  const isStandaloneLegalRoute =
    isNoTrackingRoute || isNoTrackingPath(pathnameurl);
  const suppressCookieConsent =
    isNoTrackingRoute || isCookieConsentSuppressedRoute(pathnameurl);

  useEffect(() => {
    if (suppressCookieConsent) {
      setMountCookieModal(false);
      return;
    }

    if (!isCookieConsentResolved || !isCookiePopupOpen) {
      setMountCookieModal(false);
      return;
    }

    let activated = false;
    const activate = () => {
      if (activated) return;
      activated = true;
      setMountCookieModal(true);
    };

    const events = ["pointerdown", "keydown", "touchstart"];
    const options = { passive: true };
    events.forEach((event) => window.addEventListener(event, activate, options));

    // Show consent UI once browser is idle (typically 1-3s after load).
    // Fallback to 2s timeout for Safari (no requestIdleCallback support).
    let timeout;
    if (typeof window.requestIdleCallback === "function") {
      timeout = window.requestIdleCallback(activate, { timeout: 2000 });
    } else {
      timeout = window.setTimeout(activate, 2000);
    }

    return () => {
      if (typeof window.cancelIdleCallback === "function" && typeof window.requestIdleCallback === "function") {
        window.cancelIdleCallback(timeout);
      } else {
        window.clearTimeout(timeout);
      }
      events.forEach((event) =>
        window.removeEventListener(event, activate, options)
      );
    };
  }, [isCookieConsentResolved, isCookiePopupOpen, suppressCookieConsent]);

  /* --------------------------------------------------
   *  Click outside sidebars
   * --------------------------------------------------*/
  const handleOutsideClick = useCallback(
    (e) => {
      if (
        isSidebarRightOpen &&
        !e.target?.closest(".sidebarRight") &&
        !e.target?.closest(".navbar-right-toggle")
      ) {
        setIsSidebarRightOpen(false);
      }
    },
    [isSidebarRightOpen, setIsSidebarRightOpen],
  );

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [handleOutsideClick]);

  /* --------------------------------------------------
   *  Hide-logic für Footer & Navbar
   * --------------------------------------------------*/
  const isEmailPrefs = /^\/email-praeferenzen$/.test(pathnameurl);
  const isMagicLinkAppRoute = /^\/magic-link-app\/?$/.test(pathnameurl);
  const isProfilSection = /^\/[^/]+\/profil(?:$|\/.*)/.test(pathnameurl);

  // Ausnahme: Footer NICHT verstecken auf /:id/profil/bewertung-aktualisieren (Trailing Slash optional)
  const keepFooterException =
    /^\/[^/]+\/profil\/(bewertung-aktualisieren|naechste-schritte|live-verkauf-beendet)\/?$/.test(
      pathnameurl,
    );

  const hideFooter =
    isStandaloneLegalRoute ||
    (isProfilSection && !keepFooterException) ||
    isEmailPrefs ||
    isMagicLinkAppRoute;

  // Navbar nur auf /email-praeferenzen, App-Login und Standalone-Legal-Seiten ausblenden
  const hideNavbar = isEmailPrefs || isMagicLinkAppRoute || isStandaloneLegalRoute;

  /* -------------------------------------------------- */
  return (
    <>
      <AutoArticleStructuredData />
      {!isStandaloneLegalRoute && mountToastHost && <ToastHost />}

      {!suppressCookieConsent &&
        isCookieConsentResolved &&
        isCookiePopupOpen &&
        mountCookieModal && (
        <CookiesModal
          isOpen={isCookiePopupOpen}
          setIsOpen={setIsCookiePopupOpen}
          disableClose
        />
      )}

      {isStandaloneLegalRoute && (
        <header className="legal-standalone-navbar" aria-label="Autoeinfachlos">
          <Image
            src={logo}
            alt="Autoeinfachlos logo"
            className="legal-standalone-logo"
            width={200}
            height={22}
            priority
            fetchPriority="high"
          />
        </header>
      )}

      {!hideNavbar && (
        <Navbar
          setIsSidebarRightOpen={setIsSidebarRightOpen}
          isSidebarRightOpen={isSidebarRightOpen}
        />
      )}

      {/* Mobile-Navigations-Sidebar (Hamburger). Das frühere Konto-Sidebar
          (SidebarLeft) entfällt: Initialen führen direkt zu /account, der
          Logout liegt jetzt im Navigationsmenü (SidebarRight). */}
      {!isStandaloneLegalRoute && (
        <SidebarRight
          setIsSidebarRightOpen={setIsSidebarRightOpen}
          isSidebarRightOpen={isSidebarRightOpen}
        />
      )}

      <main>{children}</main>
      {!hideFooter && <Footer />}

      {/* Support-Chatbot — blendet sich auf Funnel-/Auth-Routen selbst aus,
          siehe src/constants/chatWidgetRoutes.js */}
      {!isStandaloneLegalRoute && <ChatWidget />}
    </>
  );
}
